import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ledgerIdOf } from "./accountUser";

export default function LedgerCombo({ label, ledgers, groups = [], value, onChange, required, disabled, error, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const boxRef = useRef(null);
  const labelOf = useCallback((group) => groups.find((item) => item.value === group)?.label || group || "Other", [groups]);
  const rankOf = useCallback((group) => {
    const index = groups.findIndex((item) => item.value === group);
    return index === -1 ? groups.length : index;
  }, [groups]);
  const selected = ledgers.find((row) => ledgerIdOf(row) === String(value || "")) || null;
  const options = useMemo(() => {
    const seen = new Set();
    const rows = ledgers.filter((row) => {
      const id = ledgerIdOf(row);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const text = filtering ? query.trim().toLowerCase() : "";
    const matched = text
      ? rows.filter((row) => (
        row.name.toLowerCase().includes(text)
        || String(row.code || "").toLowerCase().includes(text)
        || labelOf(row.group).toLowerCase().includes(text)
      ))
      : rows;
    return [...matched].sort((a, b) => (
      rankOf(a.group) - rankOf(b.group)
      || labelOf(a.group).localeCompare(labelOf(b.group))
      || a.name.localeCompare(b.name)
    ));
  }, [ledgers, query, filtering, labelOf, rankOf]);
  const buckets = [];
  options.forEach((row) => {
    const title = labelOf(row.group);
    const bucket = buckets.find((item) => item.title === title);
    if (bucket) bucket.rows.push(row);
    else buckets.push({ title, rows: [row] });
  });

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const node = boxRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setDropUp(spaceBelow < 180 && spaceAbove > spaceBelow);
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, options.length]);

  const choose = (row) => {
    onChange(row ? ledgerIdOf(row) : "");
    setQuery("");
    setFiltering(false);
    setOpen(false);
  };

  return (
    <label className="tally-field">
      <span>{label}{required ? " *" : ""}</span>
      <div className={`tally-combo${error ? " invalid" : ""}${open ? " open" : ""}${dropUp ? " drop-up" : ""}`} ref={boxRef}>
        <input
          className="form-control"
          disabled={disabled}
          value={open ? query : (selected?.name || "")}
          placeholder={placeholder || "Search ledger"}
          onFocus={(event) => {
            setOpen(true);
            setFiltering(false);
            setQuery(selected?.name || "");
            event.target.select();
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setFiltering(true);
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
        />
        <div className="tally-combo-tools">
          {value && !disabled ? (
            <button type="button" className="tally-combo-tool" aria-label="Clear" title="Clear"
              onMouseDown={(event) => { event.preventDefault(); choose(null); }}>
              <i className="bi bi-x-lg" />
            </button>
          ) : null}
          <button type="button" className="tally-combo-tool" aria-label={open ? "Close list" : "Open list"} title={open ? "Close" : "Open"}
            disabled={disabled}
            onMouseDown={(event) => {
              event.preventDefault();
              setOpen((current) => !current);
              setFiltering(false);
              setQuery(selected?.name || "");
            }}>
            <i className={open ? "bi bi-chevron-up" : "bi bi-chevron-down"} />
          </button>
        </div>
        {open && !disabled && (
          <div className="tally-combo-menu">
            {buckets.length === 0 && <div className="tally-combo-empty">No matching ledger</div>}
            {buckets.map((group) => (
              <div key={group.title}>
                <div className="tally-combo-group">{group.title}</div>
                {group.rows.map((row) => (
                  <button
                    type="button"
                    key={ledgerIdOf(row)}
                    className={`tally-combo-option${ledgerIdOf(row) === String(value || "") ? " active" : ""}`}
                    title={row.name}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      choose(row);
                    }}
                  >
                    {row.name}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {error ? <span className="accounts-hint tally-error">{error}</span> : null}
    </label>
  );
}
